import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { StylesStatus } from 'app/models/users/StylesStatus';
import { Table, TableLazyLoadEvent } from 'primeng/table';
import { CommerceService } from '../../../../../../services/commerce.service';
import { MessageService } from 'primeng/api';
import { PaymentTransactions, PaymentTransactionsDto, TransactionsStatus } from '../driver-commerce-dto';
import { Utils } from 'app/utils';
import { ExportFileTypeModel } from 'app/models/export-file-type.model';
import { ExportFileTypeEnum } from 'app/models';
import * as moment from 'moment';

@Component({
  selector: 'product-transactions',
  standalone: false,
  templateUrl: './product-transactions.component.html',
  styleUrl: './product-transactions.component.scss'
})
export class ProductTransactionsComponent implements OnInit {

  @Input() showBack: boolean = false;
  @Output() goBackToDetails = new EventEmitter<boolean>();
  @Output() showDetails = new EventEmitter<number>();
  @Input() riderId!: number;

  constructor(
    private commerceService: CommerceService,
    private messageService: MessageService
  ){}

  public transactionsCols: any[] = [
    { field: 'TransactionId', header: 'Transaction ID', width: '20%' },
    { field: 'Status', header: 'Status', width: '20%' },
    { field: 'TransactionDate', header: 'Transaction Date', width: '20%' },
    { field: 'StandardAging', header: 'Standard Aging', width: '20%' },
    { field: 'ProductId', header: 'Product Id', width: '20%' },
    { field: 'ProductName', header: 'Product Name', width: '20%' },
    { field: 'AmountDeducted', header: 'Amount Deducted', width: '20%' },
    { field: 'Frequency', header: 'Frequency', width: '20%' },
    { field: 'DueDate', header: 'Due Date', width: '20%' },
    { field: 'Balance', header: 'Balance', width: '20%' }
  ];

  public rowsPerPage: number = 10;
  public rowsPerPageOptions: Array<number> = [10, 20, 30];
  public totalRecords: number = -1;

  public first: number = 0;
  public sortField: string | string[] = '';
  private sortOrder: number = 1;
  public lazyLoadHandled: boolean = false;
  public loading: boolean = false;
  public paymentTransactions: PaymentTransactions[] = [];
  public startDate: Date | null = null;
  public endDate: Date | null = null;

  public filters: any[] = [
    {name: 'By Date Range', value: 'DateRange'},
    {name: 'Product ID', value: 'DateRange'},
    {name: 'Product Name', value: 'DateRange'},
    {name: 'Status', value: 'DateRange'}
  ];

  public filter: {
    searchName: string;
    startDate: Date | null;
    endDate: Date | null;
  } = {
      searchName: '',
      startDate: null,
      endDate: null
  };
  private searchTimer: number = 0;

  // Export properties
  public showExportSidebar: boolean = false;
  public exportLoading: boolean = false;
  public ExportFileTypeModel = ExportFileTypeModel;
  public exportData: {
    fileType: ExportFileTypeEnum | null;
    startDate: Date | null;
    endDate: Date | null;
  } = { fileType: null, startDate: null, endDate: null };

  ngOnInit(): void {
    if (!this.lazyLoadHandled) {
      this.handleLazyLoad({
          first: 0,
          rows: this.rowsPerPage
      });
    }
    this.getPaymentTransactions(0);

    // Initialize export date range to last 30 days
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    this.exportData.startDate = thirtyDaysAgo;
    this.exportData.endDate = today;
  }

  public getStatusStyles(status: any): StylesStatus {
      let backgroundColor: string;
      let textColor: string;
      switch (status) {
          case TransactionsStatus.Overdue:
              backgroundColor = '#F77F00';
              textColor = '#FFF';
              break;
          case TransactionsStatus.Failed:
              backgroundColor = '#FBD2D3';
              textColor = '#EB1D25';
              break;
          case TransactionsStatus.Paid:
              backgroundColor = '#1CBA70';
              textColor = '#FFF';
              break;
          case TransactionsStatus.Penalty:
              backgroundColor = '#EB1D25';
              textColor = '#FFF';
              break;
          default:
              backgroundColor = '';
              textColor = '';
      }

      return {
        backgroundColor,
        color: textColor
      };
    }

    public getStatusText(status: any): string {
        let statusText = '';
        switch (status) {
          case TransactionsStatus.Paid:
              statusText = 'Paid';
              break;
          case TransactionsStatus.Failed:
              statusText = 'Failed';
              break;
          case TransactionsStatus.Overdue:
              statusText = 'Overdue';
              break;
          case TransactionsStatus.Penalty:
              statusText = 'Penalty';
              break;
          default:
            statusText = '';
        }
        return statusText;
      }

    public handleLazyLoad(event: TableLazyLoadEvent): void {
        const skip = event.first ? event.first : 0;

        if (event.rows) {
            this.rowsPerPage = event.rows;
        }

        if (!event.sortField) {
          this.sortField = 'Id';
          this.sortOrder = -1; // Desc
        }
        else {
            this.sortField = event.sortField;
            this.sortOrder = event.sortOrder ? event.sortOrder : 1;
        }
        this.getPaymentTransactions(skip);
        this.lazyLoadHandled = false;
    }

    goBack(){
      this.goBackToDetails.emit(false);
    }

    public getProductDetails(commerceId: number) {
        this.showDetails.emit(commerceId);
    }

    public getPaymentTransactions(skip: number): void {
      this.loading = true;
      const dto: PaymentTransactionsDto =  {
          TimeZoneOffset: Utils.getTimezoneOffset(),
          SearchName: this.filter.searchName,
          Skip: skip,
          Take: this.rowsPerPage,
          SortField: this.sortField,
          SortOrder: this.sortOrder,
          RiderId: this.riderId,
          StartDate: this.filter.startDate ? moment(this.filter.startDate).format('YYYY-MM-DDTHH:mm:ss') : undefined,
          EndDate: this.filter.endDate ? moment(this.filter.endDate).format('YYYY-MM-DDTHH:mm:ss'): undefined
      };
      this.commerceService.getPaymentTransactions(dto, this.messageService)
          .then(data => {
              if (!data.Success) {
                  this.loading = false;
                  this.messageService.add({ severity: 'error', summary: 'Error', detail: data.ErrorMessage });
                  return;
              }
              this.first = skip;
              this.totalRecords = data.TotalRecords;
              this.paymentTransactions = data.Items;
              this.paymentTransactions.forEach(item => {
                  item.TransactionDate = Utils.utcToLocaleString(item.TransactionDate);
                  item.DueDate = Utils.utcToLocaleString(item.DueDate);
              });
              this.loading = false;
          })
          .catch(err => {
              this.loading = false;
              throw err;
          });
    }

    public handleFilterChange(): void {
      if (this.searchTimer) {
          window.clearTimeout(this.searchTimer);
      }

      this.searchTimer = window.setTimeout(() => {
          this.getPaymentTransactions(0);
          this.searchTimer = 0;
      }, 1500);
    }

    // Export methods
    public openExportSidebar(): void {
        this.showExportSidebar = true;
    }

    public changedVisibleExportSidebar(increased: any): void {
        // Handle sidebar visibility change if needed
    }

    public export(): void {
        if (!this.exportData.fileType) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select file type' });
            return;
        }

        if (!this.exportData.startDate || !this.exportData.endDate) {
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Please select start and end dates' });
            return;
        }

        this.exportLoading = true;
        const dto: PaymentTransactionsDto = {
            TimeZoneOffset: Utils.getTimezoneOffset(),
            SearchName: this.filter.searchName,
            Skip: 0,
            Take: this.totalRecords, // Export all records
            SortField: this.sortField,
            SortOrder: this.sortOrder,
            RiderId: this.riderId,
            FileType: this.exportData.fileType,
            StartDate: this.filter.startDate ? moment(this.filter.startDate).format('YYYY-MM-DDTHH:mm:ss') : undefined,
            EndDate: this.filter.endDate ? moment(this.filter.endDate).format('YYYY-MM-DDTHH:mm:ss'): undefined
        };

        this.commerceService.exportPaymentTransactions(dto, this.messageService)
            .then(data => {
                this.messageService.add({ severity: 'success', summary: 'Success', detail: 'Export completed successfully' });
                Utils.saveFileResponse(data, 'Payment Transactions ' + new Date().toISOString().split('T')[0], this.exportData.fileType);
                this.exportLoading = false;
                this.showExportSidebar = false;
            })
            .catch(err => {
                this.exportLoading = false;
                this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Export failed' });
                throw err;
            });
    }

    public filterTableDirectly(): void {
    if (this.searchTimer) {
        window.clearTimeout(this.searchTimer);
        this.searchTimer = 0;
    }
    this.getPaymentTransactions(0);
}
}
